export const ERROR_CODES = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  METHOD_NOT_ALLOWED: 'METHOD_NOT_ALLOWED',
  UNSUPPORTED_MEDIA_TYPE: 'UNSUPPORTED_MEDIA_TYPE',
  RESOURCE_CONFLICT: 'RESOURCE_CONFLICT',
  IDEMPOTENCY_KEY_CONFLICT: 'IDEMPOTENCY_KEY_CONFLICT',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  REQUEST_TIMEOUT: 'REQUEST_TIMEOUT',
  RATE_LIMITED: 'RATE_LIMITED',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  UNSUPPORTED_SERVER_STATE: 'UNSUPPORTED_SERVER_STATE',
  CONFIGURATION_INVALID: 'CONFIGURATION_INVALID',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  PROJECT_NOT_FOUND: 'PROJECT_NOT_FOUND',
  PROJECT_SLUG_TAKEN: 'PROJECT_SLUG_TAKEN',
  SCRIPT_NOT_FOUND: 'SCRIPT_NOT_FOUND',
  SCRIPT_VERSION_NOT_FOUND: 'SCRIPT_VERSION_NOT_FOUND',
  RELEASE_NOT_FOUND: 'RELEASE_NOT_FOUND',
  RELEASE_NOT_PUBLISHED: 'RELEASE_NOT_PUBLISHED',
  LICENSE_NOT_FOUND: 'LICENSE_NOT_FOUND',
  LICENSE_INVALID_SECRET: 'LICENSE_INVALID_SECRET',
  LICENSE_EXPIRED: 'LICENSE_EXPIRED',
  LICENSE_REVOKED: 'LICENSE_REVOKED',
  LICENSE_SUSPENDED: 'LICENSE_SUSPENDED',
  LICENSE_STATE_INVALID: 'LICENSE_STATE_INVALID',
  LICENSE_SECRET_COLLISION: 'LICENSE_SECRET_COLLISION',
  LICENSE_MACHINE_LIMIT_REACHED: 'LICENSE_MACHINE_LIMIT_REACHED',
  LICENSE_SESSION_NOT_FOUND: 'LICENSE_SESSION_NOT_FOUND',
  LICENSE_SESSION_STALE: 'LICENSE_SESSION_STALE',
  CHECKPOINT_SESSION_NOT_FOUND: 'CHECKPOINT_SESSION_NOT_FOUND',
  CHECKPOINT_NOT_VERIFIED: 'CHECKPOINT_NOT_VERIFIED',
  CHECKPOINT_ALREADY_COMPLETED: 'CHECKPOINT_ALREADY_COMPLETED',
  CHECKPOINT_FLOW_DISABLED: 'CHECKPOINT_FLOW_DISABLED',
  CHECKPOINT_STEP_INVALID: 'CHECKPOINT_STEP_INVALID',
  CHECKPOINT_EXPIRED: 'CHECKPOINT_EXPIRED',
  PROVIDER_UNSUPPORTED: 'PROVIDER_UNSUPPORTED',
  PLAN_NOT_FOUND: 'PLAN_NOT_FOUND',
  ENTITLEMENT_LIMIT_REACHED: 'ENTITLEMENT_LIMIT_REACHED',
  SUBSCRIPTION_INACTIVE: 'SUBSCRIPTION_INACTIVE',
  ORDER_NOT_FOUND: 'ORDER_NOT_FOUND',
  ORDER_NOT_PAYABLE: 'ORDER_NOT_PAYABLE',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  REFUND_NOT_ALLOWED: 'REFUND_NOT_ALLOWED',
  REFUND_ALREADY_SETTLED: 'REFUND_ALREADY_SETTLED',
  BUILD_JOB_NOT_FOUND: 'BUILD_JOB_NOT_FOUND',
  ARTIFACT_NOT_FOUND: 'ARTIFACT_NOT_FOUND',
  ARTIFACT_NOT_READY: 'ARTIFACT_NOT_READY',
  API_KEY_INVALID: 'API_KEY_INVALID',
  API_KEY_EXPIRED: 'API_KEY_EXPIRED',
  SESSION_INVALID: 'SESSION_INVALID',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export type LocalizeParams = Record<string, string | number | boolean | null | undefined>;

export type ApiErrorDetails = Record<string, unknown>;

export const HTTP_STATUS_BY_CODE: Record<ErrorCode, number> = {
  VALIDATION_FAILED: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  UNSUPPORTED_MEDIA_TYPE: 415,
  RESOURCE_CONFLICT: 409,
  IDEMPOTENCY_KEY_CONFLICT: 409,
  PAYLOAD_TOO_LARGE: 413,
  REQUEST_TIMEOUT: 408,
  RATE_LIMITED: 429,
  SERVICE_UNAVAILABLE: 503,
  UNSUPPORTED_SERVER_STATE: 502,
  CONFIGURATION_INVALID: 500,
  INTERNAL_ERROR: 500,
  PROJECT_NOT_FOUND: 404,
  PROJECT_SLUG_TAKEN: 409,
  SCRIPT_NOT_FOUND: 404,
  SCRIPT_VERSION_NOT_FOUND: 404,
  RELEASE_NOT_FOUND: 404,
  RELEASE_NOT_PUBLISHED: 409,
  LICENSE_NOT_FOUND: 404,
  LICENSE_INVALID_SECRET: 403,
  LICENSE_EXPIRED: 409,
  LICENSE_REVOKED: 409,
  LICENSE_SUSPENDED: 409,
  LICENSE_STATE_INVALID: 409,
  LICENSE_SECRET_COLLISION: 409,
  LICENSE_MACHINE_LIMIT_REACHED: 409,
  LICENSE_SESSION_NOT_FOUND: 404,
  LICENSE_SESSION_STALE: 409,
  CHECKPOINT_SESSION_NOT_FOUND: 404,
  CHECKPOINT_NOT_VERIFIED: 409,
  CHECKPOINT_ALREADY_COMPLETED: 409,
  CHECKPOINT_FLOW_DISABLED: 409,
  CHECKPOINT_STEP_INVALID: 400,
  CHECKPOINT_EXPIRED: 409,
  PROVIDER_UNSUPPORTED: 400,
  PLAN_NOT_FOUND: 404,
  ENTITLEMENT_LIMIT_REACHED: 402,
  SUBSCRIPTION_INACTIVE: 403,
  ORDER_NOT_FOUND: 404,
  ORDER_NOT_PAYABLE: 409,
  PAYMENT_FAILED: 402,
  REFUND_NOT_ALLOWED: 409,
  REFUND_ALREADY_SETTLED: 409,
  BUILD_JOB_NOT_FOUND: 404,
  ARTIFACT_NOT_FOUND: 404,
  ARTIFACT_NOT_READY: 409,
  API_KEY_INVALID: 401,
  API_KEY_EXPIRED: 401,
  SESSION_INVALID: 401,
  SESSION_EXPIRED: 401,
};

export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  VALIDATION_FAILED: 'The request failed validation for one or more fields.',
  UNAUTHORIZED: 'Authentication is required to access this resource.',
  FORBIDDEN: 'Your account does not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  METHOD_NOT_ALLOWED: 'This HTTP method is not allowed for the requested path.',
  UNSUPPORTED_MEDIA_TYPE: 'The request content type is not supported for this resource.',
  RESOURCE_CONFLICT: 'The resource already exists in the required state.',
  IDEMPOTENCY_KEY_CONFLICT: 'This idempotency key was already used with a different request body.',
  PAYLOAD_TOO_LARGE: 'The request body exceeds the allowed size.',
  REQUEST_TIMEOUT: 'The request timed out before it could be completed.',
  RATE_LIMITED: 'Too many requests. Please retry later.',
  SERVICE_UNAVAILABLE: 'The service is temporarily unavailable.',
  UNSUPPORTED_SERVER_STATE: 'The service returned a state this client does not understand.',
  CONFIGURATION_INVALID: 'The server is missing required configuration.',
  INTERNAL_ERROR: 'An unexpected error occurred.',
  PROJECT_NOT_FOUND: 'Project {project_id} was not found.',
  PROJECT_SLUG_TAKEN: 'The project slug {slug} is already in use.',
  SCRIPT_NOT_FOUND: 'Script {script_id} was not found.',
  SCRIPT_VERSION_NOT_FOUND: 'Version {version} of script {script_id} was not found.',
  RELEASE_NOT_FOUND: 'Release {release_id} was not found.',
  RELEASE_NOT_PUBLISHED: 'Release {release_id} has not been published yet.',
  LICENSE_NOT_FOUND: 'License {license_id} was not found.',
  LICENSE_INVALID_SECRET: 'The license secret does not match this project.',
  LICENSE_EXPIRED: 'License {license_id} expired on {expires_at}.',
  LICENSE_REVOKED: 'License {license_id} has been revoked.',
  LICENSE_SUSPENDED: 'License {license_id} is suspended.',
  LICENSE_STATE_INVALID: 'License {license_id} cannot change from {from_status} to {to_status}.',
  LICENSE_SECRET_COLLISION: 'The generated license secret collided with an existing license.',
  LICENSE_MACHINE_LIMIT_REACHED: 'License {license_id} already reached its limit of {machine_limit} machines.',
  LICENSE_SESSION_NOT_FOUND: 'License session {session_id} was not found.',
  LICENSE_SESSION_STALE: 'License session {session_id} is stale. Reverify the checkpoint before continuing.',
  CHECKPOINT_SESSION_NOT_FOUND: 'Checkpoint session {session_id} was not found.',
  CHECKPOINT_NOT_VERIFIED: 'Checkpoint {step_key} has not been verified yet.',
  CHECKPOINT_ALREADY_COMPLETED: 'Checkpoint session {session_id} is already completed.',
  CHECKPOINT_FLOW_DISABLED: 'Checkpoint flow {flow_id} is disabled.',
  CHECKPOINT_STEP_INVALID: 'Checkpoint step {step_key} does not belong to flow {flow_id}.',
  CHECKPOINT_EXPIRED: 'Checkpoint session {session_id} expired on {expires_at}.',
  PROVIDER_UNSUPPORTED: 'Provider {provider_id} is not supported.',
  PLAN_NOT_FOUND: 'Plan {plan_id} was not found.',
  ENTITLEMENT_LIMIT_REACHED: 'The {feature_key} limit of {limit} for plan {plan_id} has been reached.',
  SUBSCRIPTION_INACTIVE: 'Subscription {subscription_id} is not active.',
  ORDER_NOT_FOUND: 'Order {order_id} was not found.',
  ORDER_NOT_PAYABLE: 'Order {order_id} cannot be paid in its current state.',
  PAYMENT_FAILED: 'The payment for order {order_id} failed.',
  REFUND_NOT_ALLOWED: 'Order {order_id} is not eligible for a refund.',
  REFUND_ALREADY_SETTLED: 'Refund {refund_id} has already been settled.',
  BUILD_JOB_NOT_FOUND: 'Build job {build_job_id} was not found.',
  ARTIFACT_NOT_FOUND: 'Artifact {artifact_id} was not found.',
  ARTIFACT_NOT_READY: 'Artifact {artifact_id} is not ready for download.',
  API_KEY_INVALID: 'The API key is not recognised.',
  API_KEY_EXPIRED: 'The API key has expired.',
  SESSION_INVALID: 'The session credential is not recognised.',
  SESSION_EXPIRED: 'The session has expired. Sign in again.',
};

const MISSING_INTERPOLATION = '-';

function interpolate(template: string, params: LocalizeParams | undefined): string {
  if (params === undefined) {
    return template;
  }
  const parts = template.split('{');
  if (parts.length === 1) {
    return template;
  }
  let output = parts[0] ?? '';
  for (const part of parts.slice(1)) {
    const close = part.indexOf('}');
    if (close < 0) {
      output += `{${part}`;
      continue;
    }
    const key = part.slice(0, close);
    const value = params[key];
    output += value === null || value === undefined ? MISSING_INTERPOLATION : String(value);
    output += part.slice(close + 1);
  }
  return output;
}

export function localize(code: ErrorCode, params?: LocalizeParams): string {
  return interpolate(ERROR_MESSAGES[code] ?? code, params);
}

export interface ApiErrorInit {
  message?: string | undefined;
  params?: LocalizeParams | undefined;
  details?: ApiErrorDetails | undefined;
  status?: number | undefined;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: ApiErrorDetails | undefined;
  readonly params: LocalizeParams | undefined;
  readonly verbatimMessage: boolean;

  constructor(code: ErrorCode, init: ApiErrorInit = {}) {
    const message = init.message ?? localize(code, init.params);
    super(message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.code = code;
    this.status = init.status ?? HTTP_STATUS_BY_CODE[code] ?? 500;
    this.details = init.details;
    this.params = init.params;
    this.verbatimMessage = init.message !== undefined;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

export interface ErrorEnvelopeBody {
  error: {
    code: string;
    message: string;
    details?: ApiErrorDetails | undefined;
    request_id?: string | undefined;
  };
}

export interface ErrorResponseOptions {
  requestId?: string | undefined;
  exposeInternals?: boolean | undefined;
}

export interface ErrorResponse {
  status: number;
  body: ErrorEnvelopeBody;
  code: ErrorCode;
}

export function toErrorResponse(error: unknown, options: ErrorResponseOptions = {}): ErrorResponse {
  const requestId = options.requestId;

  if (isApiError(error)) {
    const body: ErrorEnvelopeBody = {
      error: {
        code: error.code,
        message: error.message,
        ...(error.details === undefined ? {} : { details: error.details }),
        ...(requestId === undefined ? {} : { request_id: requestId }),
      },
    };
    return { status: error.status, body, code: error.code };
  }

  if (error instanceof Error) {
    const fallback: ErrorEnvelopeBody = {
      error: {
        code: ERROR_CODES.INTERNAL_ERROR,
        message: localize(ERROR_CODES.INTERNAL_ERROR),
        ...(requestId === undefined ? {} : { request_id: requestId }),
        ...(options.exposeInternals === true
          ? { details: { reason: error.name, message: error.message } }
          : {}),
      },
    };
    return {
      status: HTTP_STATUS_BY_CODE.INTERNAL_ERROR,
      body: fallback,
      code: ERROR_CODES.INTERNAL_ERROR,
    };
  }

  const unknownBody: ErrorEnvelopeBody = {
    error: {
      code: ERROR_CODES.INTERNAL_ERROR,
      message: localize(ERROR_CODES.INTERNAL_ERROR),
      ...(requestId === undefined ? {} : { request_id: requestId }),
    },
  };
  return {
    status: HTTP_STATUS_BY_CODE.INTERNAL_ERROR,
    body: unknownBody,
    code: ERROR_CODES.INTERNAL_ERROR,
  };
}

export function apiErrorFromEnvelope(
  code: string,
  message: string,
  details?: ApiErrorDetails | undefined,
): ApiError {
  const known = Object.values(ERROR_CODES).includes(code as ErrorCode);
  return new ApiError((known ? code : ERROR_CODES.INTERNAL_ERROR) as ErrorCode, {
    message,
    details,
  });
}
